import Swiper from 'swiper';
import { Navigation, Pagination, FreeMode, Thumbs, Autoplay, Zoom, Mousewheel } from 'swiper/modules';
import safeJSONParse from '../utils/safeJsonParse';
import { BREAKPOINTS, SWIPER } from '../utils/constants';

// Delay for thumb click sync (ms)
const THUMB_SYNC_DELAY = 50;

// Helper function to resolve thumb swiper from config
const resolveThumbSwiper = (thumbsSelector, thumbSwipers) => {
  if (!thumbsSelector || typeof thumbsSelector !== 'string') return null;

  // Try to find the thumb swiper element and get its Swiper instance
  const thumbElement = document.querySelector(thumbsSelector);
  if (thumbElement && thumbElement.swiper) {
    return thumbElement.swiper;
  }

  // Fallback: Extract the thumb ID from selector like "[data-swiper-thumbs=main-product-gallery]"
  const match = thumbsSelector.match(/data-swiper-thumbs=([^\]]+)/);
  if (match && thumbSwipers[match[1]]) {
    return thumbSwipers[match[1]];
  }

  return null;
};

// Helper function to sync thumb active class with main swiper
const setupThumbsSync = (mainSwiper, thumbsSwiper) => {
  // Function to update thumb active state
  const updateThumbActive = () => {
    // Use realIndex for loop mode compatibility
    const realIndex = mainSwiper.realIndex;
    const thumbSlides = thumbsSwiper.slides;
    thumbSlides.forEach((slide, index) => {
      slide.classList.toggle('swiper-slide-thumb-active', index === realIndex);
    });
  };

  // Update on various slide change events
  mainSwiper.on('slideChange', updateThumbActive);
  mainSwiper.on('slideChangeTransitionEnd', updateThumbActive);

  // Also listen for clicks on thumb slides
  thumbsSwiper.slides.forEach(slide => {
    slide.addEventListener('click', () => {
      setTimeout(updateThumbActive, THUMB_SYNC_DELAY);
    });
  });

  // Set initial state
  updateThumbActive();
};

// Standart Swiper init
const initSwiper = (slider = null) => {
  const allSwipers = slider ? [slider] : document.querySelectorAll('[data-swiper]');
  const thumbSwipers = {};

  if (!allSwipers.length) return;

  window.theme.swiperObjects = [];

  // Separate thumb swipers from main swipers - thumbs need to be initialized first
  const thumbSwipersElements = [];
  const mainSwipers = [];

  allSwipers.forEach(swiper => {
    if (swiper.dataset.swiperThumbs) {
      thumbSwipersElements.push(swiper);
    } else {
      mainSwipers.push(swiper);
    }
  });

  // Initialize thumb swipers first
  thumbSwipersElements.forEach(swiper => {
    const slides = swiper.querySelectorAll(SWIPER.SLIDE_CLASS);
    let config = swiper.dataset.swiperConfig ? safeJSONParse(swiper.dataset.swiperConfig, false) : {};
    const swiperModules = [];
    if (slides.length && config) {
      if ('navigation' in config) swiperModules.push(Navigation);
      if ('pagination' in config) swiperModules.push(Pagination);
      if ('freeMode' in config) swiperModules.push(FreeMode);
      if ('autoplay' in config) swiperModules.push(Autoplay);
      if ('mousewheel' in config) swiperModules.push(Mousewheel);
      if ('zoom' in config) swiperModules.push(Zoom);
      // Thumbs swipers need the Thumbs module too
      swiperModules.push(Thumbs);

      config = { ...config, modules: swiperModules };
      swiper.classList.add('swiper--active');
      const newSwiper = new Swiper(swiper, config);

      // Store the thumb swiper with its identifier
      const thumbId = swiper.dataset.swiperThumbs;
      thumbSwipers[thumbId] = newSwiper;

      window.theme.swiperObjects.push(newSwiper);
    }
  });

  // Then initialize main swipers
  mainSwipers.forEach(swiper => {
    const slides = swiper.querySelectorAll(SWIPER.SLIDE_CLASS);
    let config = swiper.dataset.swiperConfig ? safeJSONParse(swiper.dataset.swiperConfig, false) : {};
    const swiperModules = [];
    !config ? console.error('Error in Swiper JSON') : null;
    if (slides.length && config) {
      // Swiper operates by loading modules, that's why we check if a module is referenced in the configuration. If it is, we add it to the modules array.
      if ('navigation' in config) {
        swiperModules.push(Navigation);
      }
      if ('pagination' in config) {
        swiperModules.push(Pagination);
      }
      if ('freeMode' in config) {
        swiperModules.push(FreeMode);
      }
      if ('autoplay' in config) {
        swiperModules.push(Autoplay);
      }
      if ('mousewheel' in config) {
        swiperModules.push(Mousewheel);
      }
      if ('thumbs' in config) {
        swiperModules.push(Thumbs);
        const resolvedThumbSwiper = resolveThumbSwiper(config.thumbs?.swiper, thumbSwipers);
        if (resolvedThumbSwiper) {
          config = { ...config, thumbs: { swiper: resolvedThumbSwiper } };
        }
      }
      if ('zoom' in config) {
        swiperModules.push(Zoom);
      }

      // Adding modules to the config
      config = { ...config, ...{ modules: swiperModules } };

      swiper.classList.add('swiper--active');
      const newSwiper = new Swiper(swiper, config);

      // If this swiper has thumbs, manually sync the thumb active class
      if (newSwiper.thumbs && newSwiper.thumbs.swiper) {
        setupThumbsSync(newSwiper, newSwiper.thumbs.swiper);
      }

      if (swiper.querySelector(SWIPER.VIDEO_SLIDE)) {
        newSwiper.on('slideChangeTransitionStart', () => {
          if (!swiper.closest('[data-product-gallery]')) return;
          const videoSlides = swiper.querySelectorAll(SWIPER.VIDEO_SLIDE);
          videoSlides.forEach(slide => {
            const isActive = slide.classList.contains('swiper-slide-active');
            const video = slide.querySelector('video');
            if (!video) return;
            if (!isActive) {
              video.pause();
              video.removeAttribute('controls');
              const playBtn = video.closest('[data-video-container]')?.querySelector('[data-video-play]');
              if (playBtn) playBtn.classList.remove('opacity-0', 'pointer-events-none');
            }
          });
        });
      }
      window.theme.swiperObjects.push(newSwiper);
    }
  });
};

// Init swiper for mobile and tablet only
const initSwiperMobile = (slider = null) => {
  const desktopMediaQuery = window.matchMedia(`(min-width: ${BREAKPOINTS.DESKTOP}px)`);
  const swipers = slider ? [slider] : document.querySelectorAll('[data-swiper-mob]');

  if (!swipers.length) return;

  window.theme.swiperMobileObjects = [];

  const initSwiperOnMobile = () => {
    if (!desktopMediaQuery.matches) {
      swipers.forEach(swiper => {
        const slides = swiper.querySelectorAll(SWIPER.SLIDE_CLASS);
        let config = swiper.dataset.swiperConfig ? safeJSONParse(swiper.dataset.swiperConfig, false) : {};
        const swiperModules = [];
        !config ? console.error('Error in Swiper JSON') : null;
        if (slides.length && config) {
          if ('navigation' in config) {
            swiperModules.push(Navigation);
          }
          if ('pagination' in config) {
            swiperModules.push(Pagination);
          }
          if ('freeMode' in config) {
            swiperModules.push(FreeMode);
          }
          if ('thumbs' in config) {
            swiperModules.push(Thumbs);
          }
          if ('zoom' in config) {
            swiperModules.push(Zoom);
          }

          config = { ...config, ...{ modules: swiperModules } };
          swiper.classList.add('swiper--mob-active');
          const newSwiper = new Swiper(swiper, config);
          window.theme.swiperMobileObjects.push(newSwiper);

          if (swiper.querySelector(SWIPER.VIDEO_SLIDE)) {
            newSwiper.on('slideChangeTransitionStart', () => {
              const videoSlides = swiper.querySelectorAll(SWIPER.VIDEO_SLIDE);
              videoSlides.forEach(slide => {
                const isActive = slide.classList.contains('swiper-slide-active');
                const video = slide.querySelector('video');
                !isActive ? video.pause() : video.play();
              });
            });
          }
        }
      });
    } else {
      if (window.theme.swiperMobileObjects) {
        window.theme.swiperMobileObjects.forEach(swiper => {
          swiper.destroy();
        });
        const keys = Object.keys(window.theme.swiperMobileObjects);
        keys.forEach(key => window.theme.swiperMobileObjects[key].destroy());
        window.theme.swiperMobileObjects = [];
      }
    }
  };
  if (desktopMediaQuery) {
    initSwiperOnMobile();
  }
  desktopMediaQuery.addEventListener('change', initSwiperOnMobile);
};

export { initSwiper, initSwiperMobile };
